import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { NUMBER_300 } from '~shared/constants';
import { FiltersApplicationEventService } from '~shared/services/filters-application-event.service';
import { AttributeOption } from '~shared/models';
import { Attribute } from '~app/modules/filters/models';
import { SearchHttpService } from '~app/modules/generic/services/search-http.service';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import {
  AttributeSearchOption,
  AttributeSearchPayload,
} from '~app/modules/generic/models/attribute-search-payload.model';
import { FiltersService } from '~app/modules/filters/services/filters.service';
import { select, Store } from '@ngrx/store';
import { CatalogState, getProductsTotalCount } from '~app/modules/catalog/store';
import { Observable, Subject } from 'rxjs';
import { SpecificFilterBarService } from '~shared/services/specific-filter-bar.service';
import { RouterReducerState } from '@ngrx/router-store';
import { RouterStateUrl } from '~core/store/router/router.state';
import { getRouterState } from '~core/store';
import { BrandPLPPageState, getProductsTotalCountForBrand } from '~app/modules/brand-plp/store';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';

const SEARCH_INPUT_PLACEHOLDERS: { [name: string]: string } = {
  brand: 'FILTERS.MODAL.BRAND_SEARCH.PLACEHOLDER',
  seller: 'FILTERS.MODAL.SELLER_SEARCH.PLACEHOLDER',
};

@Component({
  templateUrl: './attribute-modal-new-design.component.html',
  styleUrls: ['./attribute-modal-new-design.component.scss'],
})
export class AttributeModalNewDesignComponent implements OnInit, OnDestroy {
  attribute: Attribute;
  attributeOptions: AttributeOption[];
  isMobileOrTablet: boolean = false;
  list: AttributeSearchOption[];
  optionSearchFormControl: FormControl = new FormControl('');
  searchInputPlaceHolder;
  searchLoading: boolean;
  selectedOptions: AttributeOption[];
  isSpecificAttribute: boolean;
  isBrandOrSellerAttribute: boolean;
  productsTotalCount$: Observable<number>;

  private activeRoute$: Observable<RouterStateUrl> = this.store.pipe(select(getRouterState));
  private unsubscribe$ = new Subject();

  constructor(
    private dialogRef: MatDialogRef<AttributeModalNewDesignComponent>,
    private filtersApplicationEventService: FiltersApplicationEventService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      filterFields: any;
      attribute: Attribute;
      isMobileOrTablet: boolean;
      queryParams: { [key: string]: any };
      isSpecificAttribute: boolean;
    },
    private searchHttpService: SearchHttpService,
    private filtersService: FiltersService,
    private store$: Store<CatalogState>,
    private brandStore$: Store<BrandPLPPageState>,
    private specificFilterBarService: SpecificFilterBarService,
    private store: Store<RouterReducerState>,
    private productsListUrlService: ProductsListUrlService
  ) {}

  ngOnInit(): void {
    const { attribute, isMobileOrTablet } = this.data;
    this.isSpecificAttribute = this.data?.isSpecificAttribute;
    this.isBrandOrSellerAttribute = this.attribute?.id === 'brand' || this.attribute?.id === 'seller';

    this.isMobileOrTablet = isMobileOrTablet;
    this.attribute = attribute;

    this.list = this.searchHttpService.mapAttributesOptions(this.attribute?.options)?.grouppedAttributeOptions;

    this.optionSearchFormControl.valueChanges
      .pipe(debounceTime(NUMBER_300), takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.searchAttributes();
      });

    this.selectedOptions = {
      ...this.data?.filterFields,
    };

    this.searchInputPlaceHolder = SEARCH_INPUT_PLACEHOLDERS[this.attribute?.id];
    this.productsTotalCount$ = this.productsListUrlService.isBrandPLPPage()
      ? this.brandStore$.pipe(select(getProductsTotalCountForBrand))
      : this.store$.pipe(select(getProductsTotalCount));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  searchAttributes(): void {
    this.searchLoading = true;
    this.searchHttpService
      .attributesSearch(this.attribute?.id, this.optionSearchFormControl?.value, this.data?.queryParams)
      .pipe(
        takeUntil(this.unsubscribe$),
        map((attributes: Attribute[]) => this.searchHttpService.mapAttributesOptions(attributes[0]?.options))
      )
      .subscribe(({ grouppedAttributeOptions, total }: AttributeSearchPayload) => {
        this.searchLoading = false;
        this.attributeOptions = this.attribute.options;
        this.list = grouppedAttributeOptions;
      });
  }

  onClose(): void {
    if (this.isSpecificAttribute) {
      const selectedAttrs = Object.entries(this.data?.filterFields)
        .filter(([_, value]) => value)
        .reduce((acc, [key, _value]) => {
          const [attrType, attrValue] = key.split('--');
          const nextIndex = Object.keys(acc[`${attrType}`] || {}).length;

          return {
            ...acc,
            [`${attrType}`]: { ...acc[`${attrType}`], [`${nextIndex}`]: attrValue },
          };
        }, {});

      this.filtersService.filterByAttribute(
        {
          selectedAttrs,
          attributeId: this.attribute?.id,
          content: '',
        },
        this.attribute?.id
      );

      this.dialogRef.close();

      return;
    }
    const dialogResult = {
      attribute: this.attribute,
      formValue: {
        ...this.data?.filterFields,
      },
    };

    this.dialogRef.close(dialogResult);
  }

  onFiltersApplied(): void {
    const dialogResult = {
      attribute: this.attribute,
      formValue: {
        ...this.selectedOptions,
      },
    };

    this.dialogRef.close(dialogResult);
  }

  clearAttributeSearch(): void {
    this.optionSearchFormControl.reset();
  }

  onShowResults(): void {
    this.dialogRef.close();
    this.specificFilterBarService.toggleFilterBar(null);
  }

  onOptionToggle(toggledAttributeOption: AttributeOption, $event: any): void {
    this.selectedOptions[`${this.attribute?.id}--${toggledAttributeOption?.value}`] = $event.target.checked;

    if (this.isMobileOrTablet) {
      const selectedAttrs = Object.entries(this.selectedOptions)
        .filter(([_, value]) => value)
        .reduce((acc, [key, _value]) => {
          const [attrType, attrValue] = key.split('--');
          const nextIndex = Object.keys(acc[`${attrType}`] || {}).length;

          return {
            ...acc,
            [`${attrType}`]: { ...acc[`${attrType}`], [`${nextIndex}`]: attrValue },
          };
        }, {});

      this.filtersService.filterByAttribute(
        {
          selectedAttrs,
          attributeId: this.attribute?.id,
          content: toggledAttributeOption?.value,
        },
        this.attribute?.id
      );

      this.filtersApplicationEventService.attributeFromModal(this.attribute.id, toggledAttributeOption.name);
    }
  }
}
